import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { AgUiEvent } from '../../types/gateway-chat-types';
import { ConnectionStatus } from '../../types/gateway-chat-types';

export interface UseChatStreamConnectionOptions {
  ensureSession: () => Promise<boolean>;
  buildStreamUrl: (conversationId: string) => string;
  onEvent: (event: AgUiEvent) => void;
  onStatusChange: (status: ConnectionStatus) => void;
  onResetState: () => void;
}

export interface ChatStreamConnectionState {
  connectToConversation: (conversationId: string) => Promise<void>;
  disconnect: () => void;
  activeConversationRef: MutableRefObject<string>;
}

export const useChatStreamConnection = ({
  ensureSession,
  buildStreamUrl,
  onEvent,
  onStatusChange,
  onResetState,
}: UseChatStreamConnectionOptions): ChatStreamConnectionState => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const activeConversationRef = useRef<string>('');

  const disconnect = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    onStatusChange('disconnected');
  }, [onStatusChange]);

  useEffect(() => () => disconnect(), [disconnect]);

  const connectToConversation = useCallback(
    async (conversationId: string) => {
      if (!conversationId) {
        return;
      }

      if (activeConversationRef.current === conversationId && eventSourceRef.current) {
        return;
      }

      activeConversationRef.current = conversationId;
      onResetState();
      disconnect();
      onStatusChange('connecting');

      try {
        await ensureSession();
      } catch (error) {
        console.error('Failed to prepare conversation for SSE connection:', error);
        onStatusChange('disconnected');
        return;
      }

      const streamUrl = buildStreamUrl(conversationId);
      const eventSource = new EventSource(streamUrl, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('message', (event) => {
        try {
          const parsedEvent: AgUiEvent = JSON.parse(event.data);
          onEvent(parsedEvent);
        } catch (err) {
          console.error('Failed to parse message event:', err);
        }
      });

      eventSource.onerror = (event) => {
        console.error('SSE connection error:', event);
        onStatusChange('disconnected');
      };

      eventSource.onopen = () => {
        onStatusChange('connected');
      };

      eventSource.addEventListener('error', (event: MessageEvent) => {
        try {
          if (!event.data) {
            return;
          }
          const data = JSON.parse(event.data);
          if (data.error && data.detail) {
            console.error('SSE authentication error:', data.detail);
            onStatusChange('disconnected');
          }
        } catch (err) {
          console.error('SSE stream error:', err);
        }
      });
    },
    [buildStreamUrl, disconnect, ensureSession, onEvent, onResetState, onStatusChange]
  );

  return {
    connectToConversation,
    disconnect,
    activeConversationRef,
  };
};
